import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useMapContext } from './MapContext';
import { Card, Icon, Button, Progress, Message } from 'semantic-ui-react';
import { unByKey } from 'ol/Observable';
import { fromLonLat, getPointResolution } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Style from 'ol/style/Style';
import { Circle as CircleStyle, Fill, Stroke, Text } from 'ol/style';
import compassSvg from '../assets/images/compass.svg';
import './MapOnlyView.css';
import SidebarWrapper from './SidebarWrapper';
import { buildMapsUrl } from '../utils/apiConfig';
import {
  downloadOfflinePackage,
  decompressGzip,
  saveOfflinePackage,
  getOfflinePackage,
  hasOfflinePackage,
  deleteOfflinePackage,
  parseOfflinePackage,
  parseMvtPackage
} from '../utils/offlineMapCache';
import {
  createOfflineLayersByType,
  createOfflineMvtLayer,
  createOceanBackgroundLayer,
  getOfflinePackageStats
} from '../utils/offlineVectorLayer';
import { onSpritesLoaded, preloadSprites, setOfflineInteracting } from '../utils/simpleOfflineStyle';
import { sendMessageToServiceWorker } from '../serviceWorkerRegistration';

const MAP_ONLY_DOWNLOAD_MODES = {
  OFFLINE: 'offline',
  JPG_125_Q85_WITH_QR: 'jpg_125dpi_q85_with_qr',
  JPG_125_Q85_NO_QR: 'jpg_125dpi_q85_no_qr',
};

/**
 * MapOnlyView Component
 *
 * A focused map view component that displays a single customer map with:
 * - Clickable POIs (Points of Interest)
 * - Map metadata (name, area, zoom levels)
 * - Scale display
 * - Orientation/rotation indicator
 * - POI selection and details
 *
 * @param {Object} mapData - Customer map object with properties and mappoints
 * @param {Function} onPOISelect - Callback when POI is clicked (optional)
 * @param {Boolean} isMobile - Mobile display flag
 * @param {Function} onClose - Callback to close the map view (optional)
 */
export default function MapOnlyView({
  mapData,
  tourMapContext = null,
  onPOISelect,
  isMobile = false,
  onClose,
  sidebarConfig,
  onHeightChange, // Add callback for height changes
  isVisible = true,
  setIsVisible
}) {
  const mapCtx = useMapContext();
  const {
    onMarkerClick,
    selectMarker,
    deselectMarker,
    addMappoints,
    fitMapToBoundaries,
    fitToMarkerSource,
    isMapReady,
    clearLayer,
    getMap
  } = mapCtx;

  const [selectedPOI, setSelectedPOI] = useState(null);
  const [poiViewMode, setPoiViewMode] = useState('vertical');
  const [mapRotation, setMapRotation] = useState(0);
  const [scaleText, setScaleText] = useState('0');
  const [scaleWidth, setScaleWidth] = useState(100);
  const layoutRef = useRef(null);

  // Offline map state
  const [offlineAvailable, setOfflineAvailable] = useState(false);
  const [offlineActive, setOfflineActive] = useState(false);
  const [offlineDownloading, setOfflineDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [offlineError, setOfflineError] = useState(null);
  const [offlineLayers, setOfflineLayers] = useState(null);
  const [offlineStats, setOfflineStats] = useState(null);
  const [savedTileLayers, setSavedTileLayers] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const offlineInteractionKeysRef = useRef([]);
  const mapOnlyMarkersLayerRef = useRef(null);
  const mapOnlyMarkersSourceRef = useRef(null);
  const setOfflineTileCaching = useCallback(async (enabled) => {
    await sendMessageToServiceWorker({
      type: 'SET_OFFLINE_TILE_CACHING',
      enabled,
    });
  }, []);

  // Extract map points from map data
  const mappoints = useMemo(() => {
    return mapData?.properties?.mappoints || [];
  }, [mapData]);
  const dockingPoi = useMemo(() => {
    const candidate = tourMapContext?.dockingPoi;
    if (!candidate?.center?.coordinates || !Array.isArray(candidate.center.coordinates)) {
      return null;
    }
    return {
      ...candidate,
      number: null,
      markerLabel: candidate.markerLabel || '\u2693\uFE0E',
      markerLabelType: candidate.markerLabelType || 'docking',
      isDockingMarker: true,
      isSyntheticDockingPoi: true,
    };
  }, [tourMapContext]);
  const displayedPois = useMemo(() => {
    const regularPois = Array.isArray(mappoints)
      ? mappoints
        .slice()
        .sort((a, b) => (a?.number || 0) - (b?.number || 0))
      : [];

    if (dockingPoi) {
      return [...regularPois, dockingPoi];
    }

    return regularPois;
  }, [dockingPoi, mappoints]);
  const getPoiMarkerId = useCallback((poi) => {
    if (!poi?.id && poi?.id !== 0) return null;
    return `mp-${poi.id}`;
  }, []);
  const findPoiByMarkerId = useCallback((markerId) => (
    displayedPois.find((poi) => getPoiMarkerId(poi) === markerId) || null
  ), [displayedPois, getPoiMarkerId]);
  const getMapOnlyMarkerFeature = useCallback((poiOrMarkerId) => {
    const markerId = typeof poiOrMarkerId === 'string' ? poiOrMarkerId : getPoiMarkerId(poiOrMarkerId);
    if (!markerId) return null;
    return mapOnlyMarkersSourceRef.current?.getFeatureById(markerId) || null;
  }, [getPoiMarkerId]);
  const setMapOnlyMarkerSelected = useCallback((poiOrMarkerId, selected) => {
    const feature = getMapOnlyMarkerFeature(poiOrMarkerId);
    if (!feature) return;
    feature.set('selected', Boolean(selected));
    feature.changed();
  }, [getMapOnlyMarkerFeature]);

  useEffect(() => {
    if (!selectedPOI) return;
    const stillExists = displayedPois.some((poi) => poi.id === selectedPOI.id);
    if (!stillExists) {
      setSelectedPOI(null);
    }
  }, [displayedPois, selectedPOI]);

  const getPoiTitle = useCallback((poi) => (
    poi?.online_title || poi?.name || 'Unnamed'
  ), []);
  const mapOnlyMarkerStyle = useCallback((feature) => {
    const number = feature.get('number');
    const markerLabelType = feature.get('markerLabelType');
    const markerLabel = feature.get('markerLabel');
    const isDockingMarker = feature.get('isDockingMarker');
    const selected = Boolean(feature.get('selected'));

    if (isDockingMarker || markerLabelType === 'docking') {
      const anchorLabel = String(markerLabel || '\u2693\uFE0E');
      return new Style({
        image: new CircleStyle({
          radius: selected ? 16 : 14,
          fill: new Fill({ color: selected ? '#ff5a00' : '#007bff' }),
          stroke: new Stroke({ color: '#ffffff', width: selected ? 4 : 3 }),
        }),
        text: new Text({
          text: anchorLabel,
          fill: new Fill({ color: '#ffffff' }),
          font: 'bold 14px "Segoe UI Symbol", "Noto Sans Symbols 2", "Arial Unicode MS", sans-serif',
          textAlign: 'center',
          textBaseline: 'middle',
          offsetY: 0,
        }),
      });
    }

    const radius = number ? 12 : 8;
    return new Style({
      image: new CircleStyle({
        radius: selected ? radius + 4 : radius,
        fill: new Fill({ color: selected ? '#ff5a00' : '#007bff' }),
        stroke: new Stroke({ color: '#ffffff', width: selected ? 4 : 2 }),
      }),
      text: number ? new Text({
        text: String(number),
        fill: new Fill({ color: '#ffffff' }),
        font: number > 9 ? 'bold 10px Arial' : 'bold 12px Arial',
        textAlign: 'center',
        textBaseline: 'middle',
      }) : undefined,
    });
  }, []);
  const configuredDownloadMode = useMemo(() => {
    const rawMode = String(
      import.meta.env.VITE_MAP_ONLY_DOWNLOAD_MODE || MAP_ONLY_DOWNLOAD_MODES.OFFLINE
    ).trim().toLowerCase();
    if (Object.values(MAP_ONLY_DOWNLOAD_MODES).includes(rawMode)) {
      return rawMode;
    }
    return MAP_ONLY_DOWNLOAD_MODES.OFFLINE;
  }, []);

  // Get offline package URL from map data
  const offlinePackageUrl = useMemo(() => {
    const baseUrl = "https://qrmaps.nbg1.your-objectstorage.com/";
    const path = mapData?.properties?.offline_package_path || mapData?.offline_package_path;
    if (path) {
      // Path already includes 'exported_maps/' prefix from the backend
      return `${baseUrl}${path}`;
    }
    return mapData?.properties?.offline_package_url || mapData?.offline_package_url || null;
  }, [mapData]);

  const offlinePackageFormat = useMemo(() => {
    return mapData?.properties?.offline_package_format || mapData?.offline_package_format || 'mvt';
  }, [mapData]);

  const mapId = useMemo(() => {
    return mapData?.id || mapData?.properties?.id;
  }, [mapData]);
  const jpg125dpiQ85WithQrUrl = useMemo(() => {
    const path = mapData?.properties?.jpg_125dpi_q85_path || mapData?.jpg_125dpi_q85_path;
    return path ? buildMapsUrl(path) : null;
  }, [mapData]);
  const jpg125dpiQ85NoQrUrl = useMemo(() => {
    const path = mapData?.properties?.jpg_125dpi_q85_path_no_qr || mapData?.jpg_125dpi_q85_path_no_qr;
    return path ? buildMapsUrl(path) : null;
  }, [mapData]);
  const configuredDownloadAction = useMemo(() => {
    if (configuredDownloadMode === MAP_ONLY_DOWNLOAD_MODES.JPG_125_Q85_WITH_QR) {
      return {
        type: 'direct',
        href: jpg125dpiQ85WithQrUrl,
        icon: 'file image',
        title: 'Download the 125 DPI JPG with QR code',
      };
    }
    if (configuredDownloadMode === MAP_ONLY_DOWNLOAD_MODES.JPG_125_Q85_NO_QR) {
      return {
        type: 'direct',
        href: jpg125dpiQ85NoQrUrl,
        icon: 'file image outline',
        title: 'Download the 125 DPI JPG without QR code',
      };
    }
    return {
      type: 'offline',
      href: offlinePackageUrl,
      icon: 'database',
      title: 'Download or open the pregenerated offline package',
    };
  }, [configuredDownloadMode, jpg125dpiQ85NoQrUrl, jpg125dpiQ85WithQrUrl, offlinePackageUrl]);
  const showDirectDownloadButton = configuredDownloadAction.type === 'direct' && Boolean(configuredDownloadAction.href);

  // Preload icon sprites once for this view lifecycle.
  useEffect(() => {
    preloadSprites();
  }, []);

  useEffect(() => {
    const map = getMap();
    if (!map) return undefined;

    if (!mapOnlyMarkersSourceRef.current) {
      mapOnlyMarkersSourceRef.current = new VectorSource();
    }

    if (!mapOnlyMarkersLayerRef.current) {
      mapOnlyMarkersLayerRef.current = new VectorLayer({
        source: mapOnlyMarkersSourceRef.current,
        zIndex: 75,
        style: mapOnlyMarkerStyle,
      });
    } else {
      mapOnlyMarkersLayerRef.current.setStyle(mapOnlyMarkerStyle);
    }

    const layer = mapOnlyMarkersLayerRef.current;
    const layers = map.getLayers().getArray();
    if (!layers.includes(layer)) {
      map.addLayer(layer);
    }

    return () => {
      if (map && layer && map.getLayers().getArray().includes(layer)) {
        map.removeLayer(layer);
      }
    };
  }, [getMap, mapOnlyMarkerStyle]);

  // Check if offline package is available in cache
  useEffect(() => {
    if (!mapId) return;

    hasOfflinePackage(mapId).then(available => {
      setOfflineAvailable(available);
    }).catch(err => {
      console.error('Error checking offline package:', err);
    });
  }, [mapId]);

  // Download offline package
  const handleDownloadOffline = useCallback(async () => {
    if (!offlinePackageUrl || !mapId) {
      console.error('Offline download blocked: missing URL or mapId', {
        offlinePackageUrl,
        mapId,
        mapDataId: mapData?.id,
      });
      setOfflineError('No offline package URL available');
      return;
    }

    setOfflineDownloading(true);
    setOfflineError(null);
    setDownloadProgress(0);

    try {
      // Download compressed package
      console.log('Downloading offline package', {
        url: offlinePackageUrl,
        mapId,
        mapName: mapData?.properties?.area_name || mapData?.area_name,
      });
      const compressedData = await downloadOfflinePackage(
        offlinePackageUrl,
        (percent) => setDownloadProgress(percent)
      );

      console.log('Downloaded offline package', {
        bytes: compressedData?.byteLength,
        mapId,
      });

      // Decompress
      console.log('Decompressing offline package', { mapId });
      const decompressedData = await decompressGzip(compressedData);
      console.log('Decompressed offline package', {
        bytes: decompressedData?.byteLength,
        mapId,
      });

      // Save to IndexedDB
      console.log('Saving offline package to cache', { mapId });
      await saveOfflinePackage(mapId, decompressedData, {
        mapName: mapData?.properties?.area_name || mapData?.area_name,
        url: offlinePackageUrl,
        format: offlinePackageFormat,
        version: mapData?.properties?.offline_package_version || mapData?.offline_package_version
      });

      setOfflineAvailable(true);
      setOfflineDownloading(false);
      setDownloadProgress(100);

      console.log('Offline package saved successfully', { mapId });
    } catch (error) {
      console.error('Error downloading offline package', {
        mapId,
        url: offlinePackageUrl,
        message: error?.message,
        name: error?.name,
        stack: error?.stack,
      });
      setOfflineError(error.message);
      setOfflineDownloading(false);
    }
  }, [offlinePackageUrl, mapId, mapData]);

  // Load offline package and activate offline mode
  const handleActivateOffline = useCallback(async () => {
    if (!mapId || !isMapReady()) return;

    try {
      // Get cached package
      const packageRecord = await getOfflinePackage(mapId);
      if (!packageRecord) {
        setOfflineError('Offline package not found in cache');
        return;
      }

      // Parse package
      const packageFormat = packageRecord.format || offlinePackageFormat;
      const map = getMap();
      if (!map) return;

      // Save and remove existing tile layers (keep vector layers for POIs/markers)
      const existingLayers = map.getLayers().getArray().slice();
      const tileLayers = [];

      existingLayers.forEach(layer => {
        if (layer instanceof TileLayer) {
          tileLayers.push(layer);
          map.removeLayer(layer);
        }
      });

      // Save tile layers for restoration
      setSavedTileLayers(tileLayers);
      console.log('Saved', tileLayers.length, 'tile layers for later restoration');
      console.log('Kept', existingLayers.length - tileLayers.length, 'vector layers (markers/POIs)');

      let poiLayer = null;
      let poiRenderLayer = null;
      const hidePoiDuringInteraction = packageFormat !== 'mvt';
      if (packageFormat === 'mvt') {
        const mvtPackage = parseMvtPackage(packageRecord.data);
        console.log('Loaded offline MVT package:', mvtPackage);
        const hasOfflineSeaPolygons = Boolean(
          mvtPackage?.header?.metadata?.has_offline_sea_polygons
        );

        let oceanLayer = null;
        if (!hasOfflineSeaPolygons) {
          oceanLayer = createOceanBackgroundLayer({ zIndex: 0 });
          map.addLayer(oceanLayer);
        }

        const mvtLayer = createOfflineMvtLayer(mvtPackage, { zIndex: 5 });
        map.addLayer(mvtLayer);
        poiRenderLayer = mvtLayer;

        setOfflineLayers({ ocean: oceanLayer, mvt: mvtLayer });
        setOfflineStats(mvtPackage.header?.statistics || null);

        const view = map.getView();
        const zoom = view?.getZoom?.();
        const extent = view?.calculateExtent?.(map.getSize?.());
        console.log('Offline MVT view state', {
          zoom,
          extent,
          minZoom: mvtPackage.header?.metadata?.min_zoom,
          maxZoom: mvtPackage.header?.metadata?.max_zoom,
          hasTiles: mvtPackage.tileIndex?.length,
          hasOfflineSeaPolygons
        });
      } else {
        const offlinePackage = parseOfflinePackage(packageRecord.data);
        console.log('Loaded offline package:', offlinePackage);

        // Get statistics
        const stats = getOfflinePackageStats(offlinePackage);
        setOfflineStats(stats);
        console.log('Offline package stats:', stats);

        // Create vector layers
        const layers = createOfflineLayersByType(offlinePackage);
        console.log('Created layers:', Object.keys(layers));

        // Add offline vector layers
        Object.values(layers).forEach(layer => {
          map.addLayer(layer);
        });

        setOfflineLayers(layers);
        poiLayer = layers['pois'] || null;
        poiRenderLayer = poiLayer;

      }

      // Refresh POI-capable layer once sprites are ready.
      onSpritesLoaded(() => {
        console.log('Sprites loaded - refreshing offline POI rendering');
        if (poiRenderLayer) {
          poiRenderLayer.changed();
        }
      });

      setOfflineActive(true);
      await setOfflineTileCaching(true);
      console.log('Offline mode activated');

      // Reduce POI rendering while interacting
      setOfflineInteracting(false);
      const refreshPoiLayer = () => {
        if (poiRenderLayer) {
          poiRenderLayer.changed();
        }
      };
      const interactionKeys = [
        map.on('movestart', () => {
          if (!hidePoiDuringInteraction) return;
          setOfflineInteracting(true);
          refreshPoiLayer();
        }),
        map.on('moveend', () => {
          if (!hidePoiDuringInteraction) return;
          setOfflineInteracting(false);
          refreshPoiLayer();
        })
      ];
      offlineInteractionKeysRef.current = interactionKeys;
    } catch (error) {
      console.error('Error activating offline mode:', error);
      setOfflineError(error.message);
    }
  }, [mapId, isMapReady, getMap, offlinePackageFormat, setOfflineTileCaching]);

  // Deactivate offline mode
  const handleDeactivateOffline = useCallback(() => {
    if (!isMapReady()) return;

    const map = getMap();
    if (map && offlineLayers) {
      if (offlineInteractionKeysRef.current.length > 0) {
        offlineInteractionKeysRef.current.forEach((key) => unByKey(key));
        offlineInteractionKeysRef.current = [];
      }
      setOfflineInteracting(false);

      // Remove offline vector layers
      Object.values(offlineLayers).forEach(layer => {
        map.removeLayer(layer);
      });

      // Restore saved tile layers
      if (savedTileLayers && savedTileLayers.length > 0) {
        console.log('Restoring', savedTileLayers.length, 'tile layers');
        savedTileLayers.forEach(layer => {
          map.addLayer(layer);
        });
        setSavedTileLayers(null);
      } else {
        console.warn('No saved tile layers found - map may need manual refresh');
      }

      setOfflineLayers(null);
      setOfflineActive(false);
      setOfflineStats(null);
      setOfflineTileCaching(false);

      console.log('Offline mode deactivated, tile layers restored');
    }
  }, [offlineLayers, savedTileLayers, isMapReady, getMap, setOfflineTileCaching]);

  useEffect(() => {
    setOfflineTileCaching(false);

    return () => {
      if (offlineInteractionKeysRef.current.length > 0) {
        offlineInteractionKeysRef.current.forEach((key) => unByKey(key));
        offlineInteractionKeysRef.current = [];
      }
      setOfflineInteracting(false);
      setOfflineTileCaching(false);
    };
  }, [setOfflineTileCaching]);

  // Delete offline package with confirmation
  const handleDeleteOffline = useCallback(async () => {
    if (!mapId) return;

    try {
      // Deactivate if active
      if (offlineActive) {
        handleDeactivateOffline();
      }

      await deleteOfflinePackage(mapId);
      setOfflineAvailable(false);
      setShowDeleteConfirm(false);
      console.log('Offline package deleted');
    } catch (error) {
      console.error('Error deleting offline package:', error);
      setOfflineError(error.message);
      setShowDeleteConfirm(false);
    }
  }, [mapId, offlineActive, handleDeactivateOffline]);

  // Monitor map view changes (rotation, zoom, scale)
  useEffect(() => {
    if (!isMapReady()) return;

    const map = getMap();
    if (!map) return;

    const view = map.getView();

    // Listen to map rotation changes
    const updateRotation = () => {
      const rotation = view.getRotation();
      setMapRotation(rotation * (180 / Math.PI)); // Convert radians to degrees
    };


    // Calculate scale bar width and text
    const updateScale = () => {
      const resolution = view.getResolution();
      if (!resolution) return;
      const center = view.getCenter();
      if (!center) return;

      // Use point resolution at the current map center so the scale bar reflects
      // Mercator distortion at the middle of the viewport, not the equator.
      const pointResolution = getPointResolution(
        view.getProjection(),
        resolution,
        center,
        'm'
      );
      if (!pointResolution) return;

      // Find a nice round number for the scale
      const maxMeters = pointResolution * 150; // Max 150px wide scale bar
      let meters = 100;

      // Extended scale steps from 50m to 1000km
      const steps = [
        50, 100, 200, 250, 500,                                  // Meters
        1000, 2000, 2500, 5000,                                  // Kilometers (1-5km)
        10000, 20000, 25000, 50000,                              // 10-50km
        100000, 150000, 200000, 300000, 400000, 500000,          // 100-500km
        600000, 800000, 1000000                                  // 600-1000km
      ];

      for (let i = 0; i < steps.length; i++) {
        if (steps[i] > maxMeters) break;
        meters = steps[i];
      }

      // Calculate width in pixels
      const width = (meters / pointResolution);

      // Format the text
      let text;
      if (meters >= 1000) {
        const km = meters / 1000;
        // Show decimal only if needed (e.g., 2.5 km, but 5 km not 5.0 km)
        text = km.toFixed(km % 1 === 0 ? 0 : 1) + ' km';
      } else {
        text = meters + ' m';
      }

      setScaleWidth(width);
      setScaleText(text);
    };

    view.on('change:rotation', updateRotation);
    view.on('change:resolution', updateScale);
    view.on('change:center', updateScale);

    // Initial values
    updateRotation();
    updateScale();

    return () => {
      // Cleanup listeners if needed
    };
  }, [isMapReady, getMap]);

  useEffect(() => {
    const element = layoutRef.current;
    if (!element) return undefined;

    const preventZoomGesture = (event) => {
      event.preventDefault();
    };
    const preventTwoFingerTouchZoom = (event) => {
      if (event.touches?.length > 1) {
        event.preventDefault();
      }
    };

    element.addEventListener('gesturestart', preventZoomGesture, { passive: false });
    element.addEventListener('gesturechange', preventZoomGesture, { passive: false });
    element.addEventListener('touchmove', preventTwoFingerTouchZoom, { passive: false });

    return () => {
      element.removeEventListener('gesturestart', preventZoomGesture);
      element.removeEventListener('gesturechange', preventZoomGesture);
      element.removeEventListener('touchmove', preventTwoFingerTouchZoom);
    };
  }, []);

  // Add POIs to map and fit to boundaries
  useEffect(() => {
    if (!isMapReady()) return;

    clearLayer('markers');
    const localSource = mapOnlyMarkersSourceRef.current;
    if (!localSource) return;
    localSource.clear();

    const featureCollection = {
      type: 'FeatureCollection',
      features: displayedPois
        .filter((poi) => poi?.center?.type === 'Point' && Array.isArray(poi.center.coordinates))
        .map((poi) => {
          const { center, ...properties } = poi;
          return {
            type: 'Feature',
            geometry: center,
            properties,
          };
        }),
    };

    const map = getMap();
    const featureProjection = map ? map.getView().getProjection() : 'EPSG:3857';
    const features = new GeoJSON().readFeatures(featureCollection, {
      dataProjection: 'EPSG:4326',
      featureProjection,
    });

    features.forEach((feature) => {
      const id = feature.get('id');
      if (id != null) {
        feature.setId(`mp-${id}`);
      }
      feature.set('selected', selectedPOI?.id === feature.get('id'));
    });

    if (features.length) {
      localSource.addFeatures(features);
    }

    // Fit to boundaries if available, otherwise fit to markers
    if (mapData?.properties?.boundaries) {
      fitMapToBoundaries(mapData.properties.boundaries, {
        padding: [50, 50, 50, 50],
        duration: 1000
      });
    } else {
      fitToMarkerSource({
        padding: [50, 50, 50, 50],
        duration: 1000
      });
    }
  }, [displayedPois, isMapReady, clearLayer, fitMapToBoundaries, fitToMarkerSource, mapData, getMap, selectedPOI]);

  // Handle POI clicks from map
  useEffect(() => {
    if (!onMarkerClick) return;

    const unsubscribe = onMarkerClick((markerId, feature) => {
      if (!markerId) return;

      const poi = findPoiByMarkerId(markerId);

      if (poi) {
        // If component is hidden, open it first
        if (!isVisible && setIsVisible) {
          setIsVisible(true);
        }

        // Deselect previous marker if any
        setSelectedPOI(prevPOI => {
          if (prevPOI && prevPOI.id !== poi.id) {
            const previousMarkerId = getPoiMarkerId(prevPOI);
            if (previousMarkerId) {
              deselectMarker(previousMarkerId);
              setMapOnlyMarkerSelected(previousMarkerId, false);
            }
          }
          return poi;
        });

        selectMarker(markerId);
        setMapOnlyMarkerSelected(markerId, true);

        // Call external callback if provided
        if (onPOISelect) {
          onPOISelect(poi);
        }

        // Scroll POI card into view in the list
        setTimeout(() => {
          const el = document.getElementById(`poi-card-${poi.id}`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }, 100);
      }
    });

    return unsubscribe;
  }, [onMarkerClick, findPoiByMarkerId, selectMarker, deselectMarker, onPOISelect, isMobile, isVisible, setIsVisible, getPoiMarkerId, setMapOnlyMarkerSelected]);

  // Clear POI selection
  const handleClearSelection = useCallback(() => {
    if (selectedPOI) {
      const markerId = getPoiMarkerId(selectedPOI);
      if (markerId) {
        deselectMarker(markerId);
        setMapOnlyMarkerSelected(markerId, false);
      }
      setSelectedPOI(null);
    }
  }, [selectedPOI, deselectMarker, getPoiMarkerId, setMapOnlyMarkerSelected]);

  // Reset map rotation to north
  const handleResetRotation = useCallback(() => {
    const map = getMap();
    if (map) {
      map.getView().animate({ rotation: 0, duration: 500 });
    }
  }, [getMap]);

  // Get compass direction label
  const getCompassDirection = (degrees) => {
    const normalized = ((degrees % 360) + 360) % 360;
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(normalized / 45) % 8;
    return directions[index];
  };

  const renderPoiCard = (poi, carousel = false) => (
    <div
      key={poi.id}
      id={`poi-card-${poi.id}`}
      style={{
        padding: '8px 12px',
        border: selectedPOI?.id === poi.id ? '2px solid #007bff' : '1px solid #dee2e6',
        borderRadius: '10px',
        cursor: 'pointer',
        backgroundColor: selectedPOI?.id === poi.id ? '#e3f2fd' : '#fff',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        ...(carousel ? {
          flex: '0 0 auto',
          width: isMobile ? '84vw' : '340px',
          maxWidth: '100%',
          scrollSnapAlign: 'start',
        } : {}),
      }}
      onClick={() => {
        if (selectedPOI?.id === poi.id) {
          handleClearSelection();
        } else {
          if (selectedPOI) {
            const previousMarkerId = getPoiMarkerId(selectedPOI);
            if (previousMarkerId) {
              deselectMarker(previousMarkerId);
              setMapOnlyMarkerSelected(previousMarkerId, false);
            }
          }

          setSelectedPOI(poi);
          const markerId = getPoiMarkerId(poi);
          if (markerId) {
            selectMarker(markerId);
            setMapOnlyMarkerSelected(markerId, true);
          }

          if (onPOISelect) {
            onPOISelect(poi);
          }

          if (poi.center?.coordinates) {
            const map = getMap();
            if (map) {
              map.getView().animate({
                center: fromLonLat(poi.center.coordinates),
                duration: 500
              });
            }
          }
        }
      }}
      onMouseEnter={(e) => {
        if (selectedPOI?.id !== poi.id) {
          e.currentTarget.style.backgroundColor = '#f8f9fa';
          e.currentTarget.style.borderColor = '#007bff';
        }
      }}
      onMouseLeave={(e) => {
        if (selectedPOI?.id !== poi.id) {
          e.currentTarget.style.backgroundColor = '#fff';
          e.currentTarget.style.borderColor = '#dee2e6';
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: poi.isDockingMarker
            ? (selectedPOI?.id === poi.id ? '#007bff' : '#6c757d')
            : (selectedPOI?.id === poi.id ? '#007bff' : '#6c757d'),
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 'bold',
          flexShrink: 0
        }}>
          {poi.isDockingMarker ? (
            <Icon
              name='anchor'
              fitted
              style={{
                margin: 0,
                color: '#ffffff',
                fontSize: '13px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          ) : poi.number}
        </span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '14px',
            fontWeight: selectedPOI?.id === poi.id ? 'bold' : 'normal',
            color: '#212529'
          }}>
            {getPoiTitle(poi)}
          </div>
          {poi.online_title && poi.name && poi.online_title !== poi.name && (
            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
              {poi.name}
            </div>
          )}
        </div>
        {selectedPOI?.id === poi.id && (
          <Icon name='check circle' color='blue' />
        )}
      </div>

      {selectedPOI?.id === poi.id && (
        <div
          style={{
            paddingLeft: '38px',
            fontSize: '12px',
            color: '#6c757d',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          {poi.online_photo_url && (
            <img
              src={poi.online_photo_url}
              alt={getPoiTitle(poi)}
              loading="lazy"
              style={{
                width: '100%',
                maxHeight: '180px',
                objectFit: 'cover',
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}
            />
          )}
          {poi.online_text && (
            <div
              style={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.45,
                color: '#495057'
              }}
            >
              {poi.online_text}
            </div>
          )}
          {poi.location_code && (
            <div>
              <Icon name='qrcode' size='small' />
              <a
                href={`https://plus.codes/${poi.location_code}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#007bff',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  marginLeft: '4px'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                title="Open Plus Code in Google Maps"
              >
                {poi.location_code}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <SidebarWrapper
      isMobile={isMobile}
      sidebarConfig={sidebarConfig}
      onClose={() => {
        if (typeof onClose === 'function') {
          onClose();
          return;
        }
        if (typeof setIsVisible === 'function') {
          setIsVisible(false);
        }
      }}
      onToggleVisibility={setIsVisible}
      isVisible={isVisible}
      panelLabel="Map details"
      onHeightChange={onHeightChange}
      zIndex={500}
    >
      <div
        ref={layoutRef}
        className="map-only-view-layout"
        style={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top section with map name */}
        <div style={{
          padding: '12px',
          borderBottom: '1px solid #dee2e6',
          background: '#fff',
          flexShrink: 0,
        }}>
          {/* Map Name and Area */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            {showDirectDownloadButton ? (
              <Button
                as='a'
                size='small'
                icon='download'
                color='blue'
                href={configuredDownloadAction.href}
                target='_blank'
                rel='noopener noreferrer'
                title='Download map image'
                style={{
                  width: '40px',
                  minWidth: '40px',
                  height: '40px',
                  minHeight: '40px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              />
            ) : null}
            <div style={{ textAlign: 'center' }}>
            {isMobile ? (
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#212529'
              }}>
                {mapData?.properties?.area_name || mapData?.area_name || 'Map Area'} ({mapData?.properties?.country_name || mapData?.country_name || 'Unknown'})
              </div>
            ) : (
              <>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#212529',
                  marginBottom: '8px'
                }}>
                  {mapData?.properties?.area_name || mapData?.area_name || 'Map Area'}
                </div>
                <div style={{
                  fontSize: '18px',
                  color: '#6c757d'
                }}>
                  {mapData?.properties?.country_name || mapData?.country_name || 'Unknown'}
                </div>
              </>
            )}
            </div>
          </div>
        </div>

        <div style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch'
        }}>
          <div style={{ padding: '12px' }}>
            {/* Middle section - POI list */}
            <div style={{ overflowX: poiViewMode === 'carousel' ? 'auto' : 'hidden' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                marginBottom: '12px',
                paddingBottom: '8px',
                borderBottom: '2px solid #dee2e6'
              }}>
                <span style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#212529',
                }}>
                  Sights and highlights
                </span>
                <Button.Group size='small'>
                  <Button
                    type='button'
                    icon='list ul'
                    active={poiViewMode === 'vertical'}
                    title='Vertical list'
                    aria-label='Switch to vertical list'
                    onClick={() => setPoiViewMode('vertical')}
                  />
                  <Button
                    type='button'
                    icon='columns'
                    active={poiViewMode === 'carousel'}
                    title='Horizontal carousel'
                    aria-label='Switch to horizontal carousel'
                    onClick={() => setPoiViewMode('carousel')}
                  />
                </Button.Group>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: poiViewMode === 'carousel' ? 'row' : 'column',
                  gap: '8px',
                  minWidth: poiViewMode === 'carousel' ? 'max-content' : 'auto',
                  paddingBottom: poiViewMode === 'carousel' ? '4px' : 0,
                  scrollSnapType: poiViewMode === 'carousel' ? 'x proximity' : 'none',
                }}
              >
              {displayedPois.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: poiViewMode === 'carousel' ? 'row' : 'column',
                    gap: '8px',
                  }}
                >
                  {displayedPois.map((poi) => renderPoiCard(poi, poiViewMode === 'carousel'))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                  <Icon name='info circle' />
                  <p>No points of interest available</p>
                </div>
              )}
              </div>
            </div>

            {/* Bottom section - compass, offline controls, and scale */}
            <div style={{
              padding: '16px',
              marginTop: '12px',
              background: '#fff',
              borderTop: '1px solid #dee2e6',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              gap: '12px',
            }}>
              {/* Error message */}
              {offlineError && (
                <Message negative size='tiny' style={{ marginBottom: '8px', width: '100%' }}>
                  <Icon name='warning sign' />
                  {offlineError}
                </Message>
              )}

              {/* Download progress */}
              {offlineDownloading && (
                <Progress
                  percent={downloadProgress}
                  size='small'
                  indicating
                  style={{ marginBottom: '8px', width: '100%' }}
                >
                  Downloading... {downloadProgress.toFixed(0)}%
                </Progress>
              )}

              {/* Compass, Scale, and Offline Controls Row */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                justifyContent: 'center',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
              }}>
            {/* Compass SVG from assets */}
            <div
              style={{
                width: isMobile ? '60px' : '80px',
                height: isMobile ? '60px' : '80px',
                position: 'relative',
                cursor: 'pointer',
                flexShrink: 0
              }}
              onClick={handleResetRotation}
              title="Click to reset map orientation to north"
            >
              <img
                src={compassSvg}
                alt="Compass"
                style={{
                  width: '100%',
                  height: '100%',
                  transform: `rotate(${mapRotation}deg)`,
                  transition: 'transform 0.3s ease',
                }}
              />
            </div>

            {/* Scale Bar - horizontal between compass and buttons */}
            {isMobile && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                flex: 1,
                maxWidth: '150px'
              }}>
                {/* Scale line */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0',
                  justifyContent: 'center'
                }}>
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#212529',
                    minWidth: '15px'
                  }}>
                    0
                  </span>
                  <div style={{
                    position: 'relative',
                    height: '8px',
                    margin: '0 6px'
                  }}>
                    <div style={{
                      width: `${scaleWidth * 0.7}px`,
                      height: '3px',
                      background: '#212529',
                      position: 'relative'
                    }}>
                      {/* Left end mark */}
                      <div style={{
                        position: 'absolute',
                        left: '0',
                        top: '-2px',
                        width: '2px',
                        height: '7px',
                        background: '#212529'
                      }} />
                      {/* Right end mark */}
                      <div style={{
                        position: 'absolute',
                        right: '0',
                        top: '-2px',
                        width: '2px',
                        height: '7px',
                        background: '#212529'
                      }} />
                    </div>
                  </div>
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#212529',
                    minWidth: '40px',
                    textAlign: 'left'
                  }}>
                    {scaleText}
                  </span>
                </div>
              </div>
            )}

            {/* Configured customer download controls */}
            {configuredDownloadAction.type === 'offline' && offlinePackageUrl && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                flexShrink: 0,
                alignItems: 'center'
              }}>
                {/* Download/Delete Button with Confirmation */}
                {!showDeleteConfirm ? (
                  <Button
                    size='small'
                    icon={offlineAvailable ? 'trash' : 'download'}
                    content={offlineAvailable ? 'Delete' : 'Download'}
                    color={offlineAvailable ? 'red' : 'blue'}
                    onClick={offlineAvailable ? () => setShowDeleteConfirm(true) : handleDownloadOffline}
                    disabled={offlineDownloading}
                    title={offlineAvailable ? 'Delete offline map' : 'Download offline map'}
                    style={{
                      minWidth: '80px',
                      minHeight: '44px',
                      padding: '8px 12px',
                      fontSize: '11px'
                    }}
                  />
                ) : (
                  // Confirmation buttons - stacked vertically
                  <>
                    <Button
                      size='small'
                      icon='check'
                      content='Confirm'
                      color='red'
                      onClick={handleDeleteOffline}
                      title='Confirm delete'
                      style={{
                        minWidth: '80px',
                        minHeight: '44px',
                        padding: '8px 12px',
                        fontSize: '11px'
                      }}
                    />
                    <Button
                      size='small'
                      icon='close'
                      content='Cancel'
                      color='grey'
                      onClick={() => setShowDeleteConfirm(false)}
                      title='Cancel delete'
                      style={{
                        minWidth: '80px',
                        minHeight: '44px',
                        padding: '8px 12px',
                        fontSize: '11px'
                      }}
                    />
                  </>
                )}

                {/* Online/Offline Toggle Button - only show when not in delete confirmation mode */}
                {offlineAvailable && !showDeleteConfirm && (
                  <Button
                    size='small'
                    icon={offlineActive ? 'wifi' : 'database'}
                    content={offlineActive ? 'Online' : 'Offline'}
                    color={offlineActive ? 'green' : 'grey'}
                    onClick={offlineActive ? handleDeactivateOffline : handleActivateOffline}
                    disabled={offlineDownloading}
                    title={offlineActive ? 'Switch to online map' : 'Switch to offline map'}
                    style={{
                      minWidth: '80px',
                      minHeight: '44px',
                      padding: '8px 12px',
                      fontSize: '11px'
                    }}
                  />
                )}
              </div>
            )}
              </div>

              {/* Scale Bar - Desktop only (below compass) */}
              {!isMobile && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {/* Rotation info */}
                  {Math.abs(mapRotation) > 1 && (
                    <div style={{
                      fontSize: '11px',
                      color: '#6c757d',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      Rotated {Math.abs(mapRotation).toFixed(0)}° {getCompassDirection(mapRotation)}
                      <br />
                      <span style={{ fontSize: '10px', fontStyle: 'italic' }}>
                        Click compass to reset
                      </span>
                    </div>
                  )}

                  {/* Scale line */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0',
                    width: '100%',
                    justifyContent: 'center'
                  }}>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#212529',
                      minWidth: '20px'
                    }}>
                      0
                    </span>
                    <div style={{
                      position: 'relative',
                      height: '8px',
                      margin: '0 8px'
                    }}>
                      <div style={{
                        width: `${scaleWidth}px`,
                        height: '3px',
                        background: '#212529',
                        position: 'relative'
                      }}>
                        {/* Left end mark */}
                        <div style={{
                          position: 'absolute',
                          left: '0',
                          top: '-2px',
                          width: '2px',
                          height: '7px',
                          background: '#212529'
                        }} />
                        {/* Right end mark */}
                        <div style={{
                          position: 'absolute',
                          right: '0',
                          top: '-2px',
                          width: '2px',
                          height: '7px',
                          background: '#212529'
                        }} />
                      </div>
                    </div>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#212529',
                      minWidth: '50px',
                      textAlign: 'left'
                    }}>
                      {scaleText}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SidebarWrapper>
  );
}
